// Ledger — THE ONE SHAPE EVERY TABLE OF ENTRIES ON THE CONSOLE TAKES.
//
// Operator, 17 Sept 2026, looking at a party statement on a wide monitor:
// "need better UI & Clear view, currency wise statements … same currency wise
// view on money/trades screens. still the screen looks mis-aligned for
// reports/statements in each screen, dashboard/trades/money/party … maybe
// this blank space is too much and fonts etc are too small, movement type is
// too short, description lines are not in sync and not in same formats,
// fonts too small, no colors properly used".
//
// His screenshot: a statement spanning ~1800px, the sentence at the left and
// the two figures at the far right with 900px of nothing between; 12px rows,
// 10px tags and times; the kind tag a grey chip that said little and carried
// no colour; every sentence starting at a different x because the tag's width
// varied; the Reverse button floating mid-row at a different x on every row.
// The sweep of 14–15 Sept had shrunk every table for density; he has
// rejected that.
//
// THE ANSWER IS ONE PRIMITIVE. Every table that lists entries — the party
// statement, Money movements, the Trades log, Home's recent trades, a party's
// recent deals, the last-ten panels under the booking forms — takes its
// widths, sizes and colours from HERE, so a size or width fix lands
// everywhere at once ("fix once, applies everywhere"):
//
//   LEDGER        the class strings: one table size, one header, one seat per
//                 column. The kind pill owns a column of fixed width so every
//                 sentence starts on one vertical line; every figure column
//                 has a real width of its own and sits right-aligned; the
//                 sentence column is the ONE that gives way (w-full max-w-0),
//                 the whole sentence on the row's title; a block is capped at
//                 1240px so the figures sit near the words on a wide monitor.
//                 A seat never carries two sizes: the When column's WIDTH
//                 (whenW / whenStampW, on the th and the td) is one string and
//                 its TEXT (whenText, on the td alone) another, so the header
//                 reads like every other header.
//   KindPill      the coloured type pill — ONE tone map keyed by the WORD
//                 (lib/naming's movementKindLabel / ledgerTypeLabel, the
//                 Trades log's Buy / Sell / MC), so every screen colours the
//                 same word the same way. The word carries the meaning; the
//                 tone underlines it (§7 rule 10).
//   LedgerHead    a block's header line: title, closing figure, a muted note.
//   DayRow        the day separator inside a table.
//   Amount        a figure with its currency: the number in .amt, the code
//                 muted beside it — the ONE way an amount and its code are
//                 printed on a row (the Trades log's legs, the last-ten
//                 lines, a deal row's legs).
//   Delta/Figure  a signed movement and a balance, both .amt, the negative
//                 one in the negative colour — moved here from PartyStatement
//                 so Money movements uses the same Delta.
//   DealRowLine   one aligned row for a deal wherever deals are listed
//                 outside the Trades log: stamp · party · the customer's
//                 legs in words ("buys 10,000 USDT · pays 36,710 AED" — §6
//                 "a trade as one line") · @ rate · a state pill only when
//                 it says something · an action of the screen's, in a seat
//                 of its own. A grid at lg+; two lines on the phone, where a
//                 one-line grid would squeeze the party name to nothing:
//                 stamp · party ……… @ rate, then the legs alone (19 Sept 2026,
//                 "fix the phone layout for the home screen too" — the rate
//                 used to trail the legs and wrap to a third line).
//
// No screen-specific logic lives here: what a row's words are, which rows a
// screen shows, what a header ties to — that is the screen's. This file says
// only how a ledger LOOKS.

import type { ReactNode } from 'react';
import { Pill, type PillTone } from './Pill';
import { lifecycleLabel, lifecycleTone, isChequeDeal } from './DealSlideOver';
import { displayAsset, fmtAmount, fmtRate, fmtStamp } from '../lib/format';
import { partyName } from '../lib/naming';

/* ══════════════════ the class strings ══════════════════ */

const TH = 'text-left py-1.5 px-2 text-label uppercase tracking-[0.06em] text-tg-hint font-medium whitespace-nowrap';
/** The When cell's text — the td's alone; the th keeps the header face. */
const WHEN_TEXT = 'font-mono tabular text-meta text-tg-hint whitespace-nowrap';
const WHEN_W = 'w-[4.75rem] min-w-[4.75rem]';
const WHEN_STAMP_W = 'w-[6.5rem] min-w-[6.5rem]';

export const LEDGER = Object.freeze({
  /** The table: one size, a half-step up on lg. table-fixed: the declared
   *  column widths ARE the widths — an auto table let one long hand-typed
   *  reference push its column to 255px and the whole table sideways (18
   *  Sept 2026). Every table on these seats gives each column a width; the
   *  giving-way column (words) takes what is left, and truncates. */
  table:      'w-full table-fixed text-body',
  th:         TH,
  /** The header of a full-height log: stays put while the rows scroll. */
  thSticky:   TH + ' lg:sticky lg:top-0 lg:z-[1] lg:bg-panel lg:shadow-[0_1px_0_0_theme(colors.stroke.soft)]',
  td:         'py-2 px-2 align-middle',
  /** The When column's width alone — for a time ("13:01"); on the th and the td. */
  whenW:      WHEN_W,
  /** The When column's width alone — for a stamp ("Y 13:01", "10 Sep, 12:51"). */
  whenStampW: WHEN_STAMP_W,
  /** The When cell's text: mono, a step down, muted. The td only. */
  whenText:   WHEN_TEXT,
  /** A time cell: its width and its text, for a td. */
  when:       WHEN_TEXT + ' ' + WHEN_W,
  /** A stamp cell: its width and its text, for a td. */
  whenStamp:  WHEN_TEXT + ' ' + WHEN_STAMP_W,
  /** The type pill's own column — so every sentence starts on one line. */
  kind:       'w-[8.5rem] min-w-[8.5rem] whitespace-nowrap',
  /** The one column that gives way: under table-fixed it is the ONE column
   *  with no width, so it takes whatever the sized columns leave (the old
   *  "w-full max-w-0" trick was for auto layout, and under fixed layout it
   *  shrank the column to nothing — the Description overlapped the Account
   *  column, 18 Sept 2026). The sentence inside is a div with "truncate",
   *  the whole sentence on the row's title; overflow-hidden keeps a long
   *  word from spilling into the next seat. */
  /** 19 Sept 2026 (operator, on Home: "can be cleaner look here, and every
   *  such screen — clear spacings"): the words column has a width of its
   *  own now, and the table's slack goes to a trailing empty seat (slack),
   *  so the figure columns sit beside the words at the same gaps on every
   *  screen and every width, instead of 700px away on a wide monitor. */
  words:      'w-[22rem] overflow-hidden',
  /** A status pill's column — narrower than a type pill's. */
  status:     'w-[6.5rem] min-w-[6.5rem] whitespace-nowrap',
  /** The trailing empty column that takes whatever width is left. */
  slack:      '',
  /** Every figure column; the figure inside is .amt at the table size. */
  fig:        'w-[10rem] min-w-[10rem] pl-4 text-right whitespace-nowrap',
  /** A figure column that CARRIES ITS CURRENCY (a journal's Movement and
   *  Balance): one seat holds every currency the entry moved, so the code
   *  rides muted on each figure and the seat is a step wider than `fig`,
   *  whose block header names the one currency instead. The party journal
   *  sized this first on 22 Sept 2026; Home's took the same seat on 24 Sept,
   *  and one constant is what keeps the two journals on one grid. */
  figWide:    'w-[11rem] min-w-[11rem] pl-4 text-right whitespace-nowrap',
  /** A per-row action button's own column. */
  act:        'w-[5.5rem] min-w-[5.5rem] pl-2 text-right whitespace-nowrap',
  /** The day separator cell. */
  day:        'pt-3 pb-1 px-2 text-label uppercase tracking-[0.06em] text-tg-hint',
  /** A statement block's width cap — bounds the blank space on a wide monitor. */
  block:      'max-w-[1240px]',
  /** A row that is not live (reversed, a reversal). */
  rowDim:     'opacity-70',
  // The phone cards.
  card:       'py-2 border-b border-tg-secondary',
  /** The phone card's two lines: the pill and the whole sentence (two lines
   *  of it at most), then the figures — time · movement · [action] · balance
   *  — a half-step smaller so the widest desk figures share one line. */
  cardLine1:  'flex items-start gap-2 min-w-0 text-body leading-snug',
  cardLine2:  'flex items-baseline gap-x-3 mt-1 text-meta',
  // A LIST LINE outside a table (a deal row, a last-ten line): two lines —
  // lineLead holds the stamp and the name, lineTail the figures — and, where
  // the row is wide enough, a grid at lg+ (lineGrid, the caller naming its
  // columns with lg:grid-cols-[…]) into which both lines dissolve (lineSeat)
  // so their cells take the grid's seats. A narrow card keeps the two lines.
  line:       'px-2 py-2 text-body',
  lineGrid:   'lg:grid lg:items-baseline lg:gap-x-3',
  lineSeat:   'lg:contents',
  lineLead:   'flex items-baseline gap-2 min-w-0',
  lineTail:   'flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 mt-0.5',
});

/* ══════════════════ the kind pill ══════════════════ */

/** ONE MAP, KEYED BY THE WORD. The keys are the labels lib/naming produces
 *  (movementKindLabel on Money movements, ledgerTypeLabel on the statement)
 *  and the Trades log's three kind words, so the same word wears the same
 *  tone on every screen. Where two screens name one entry differently —
 *  Money's "Owner" is the statement's "Capital" / "Drawing"; Money files a
 *  profit share under "Adjustment", the statement names it — the two words
 *  wear one tone, so the entry is coloured alike wherever it is read.
 *  Buy and Sell wear the money's own law (operator, 21 Sept 2026, "buy &
 *  sell colors same?" → "ship it"): a customer's Buy brings money in —
 *  green, as the Money in pill; a Sell pays it out — red, as Money out. The
 *  14 Sept ruling (item 72: no colour by direction) is superseded by the
 *  operator's word; the word still carries the direction, the tone now
 *  underlines it the way it does on every money row. Anything unmapped is
 *  neutral: a colour is never guessed. */
const KIND_TONE: Readonly<Record<string, PillTone>> = {
  'Money in':        'good',
  'Money out':       'bad',
  'Money':           'neutral',
  'Trade':           'info',
  'Buy':             'good',
  'Sell':            'bad',
  'Margin':          'info',
  'Between parties': 'neutral',
  'Move':            'neutral',
  'Adjustment':      'warn',
  'Correction':      'warn',
  'Reclass':         'warn',
  'Profit share':    'warn',
  'Cost':            'gold',
  'Owner':           'info',
  'Capital':         'info',
  'Drawing':         'info',
  'Cheque':          'gold',
  'Cheque fee':      'gold',
  'MC':              'gold',
  'Opening':         'neutral',
  'Brought forward': 'neutral',
};

export function kindTone(label: string): PillTone {
  return KIND_TONE[label] ?? 'neutral';
}

/** The coloured type pill: the word says what kind of entry this is, the
 *  tone underlines it. A step larger than a status pill — it sits in a
 *  13.5px row and must not be the smallest thing on it. */
export function KindPill({ label, className = '' }: { label: string; className?: string }) {
  return <Pill tone={kindTone(label)} size="md" className={className}>{label}</Pill>;
}

/* ══════════════════ the block header ══════════════════ */

/** A block's header line: the title at the left; the figure and a muted
 *  note at the right — the note AHEAD of the figure when it introduces it
 *  ("Balance on 12 Sep  1,234"), after it otherwise. Every currency block
 *  on every screen uses it. */
export function LedgerHead({ title, figure, note, noteFirst = false, className = '' }: {
  title: ReactNode;
  figure?: ReactNode;
  note?: ReactNode;
  noteFirst?: boolean;
  className?: string;
}) {
  const noteEl = note ? <span className="text-meta text-tg-hint">{note}</span> : null;
  const figureEl = figure ? <span className="amt text-body">{figure}</span> : null;
  return (
    <div className={`flex items-baseline justify-between gap-3 px-2 pb-1.5 border-b border-tg-secondary ${className}`}>
      <span className="font-medium text-body">{title}</span>
      {(figure || note) && (
        <span className="flex flex-wrap items-baseline justify-end gap-x-2 text-right min-w-0">
          {noteFirst ? <>{noteEl}{figureEl}</> : <>{figureEl}{noteEl}</>}
        </span>
      )}
    </div>
  );
}

/* ══════════════════ the day row ══════════════════ */

/** The day separator inside a table, across every column. */
export function DayRow({ label, colSpan }: { label: string; colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className={LEDGER.day}>{label}</td>
    </tr>
  );
}

/* ══════════════════ the figures ══════════════════ */

/** An amount with its currency: the number in .amt, the code muted beside
 *  it. The one rendering of "69,293.50 USDT" on a row. */
export function Amount({ value, asset }: { value: string | number; asset: string }) {
  return (
    <span className="whitespace-nowrap">
      <span className="amt">{fmtAmount(value, asset)}</span>{' '}
      <span className="text-tg-hint">{displayAsset(asset)}</span>
    </span>
  );
}

/** A balance in the sign law's colour: the one negative token, and nothing
 *  for a positive one — the words carry the meaning. The desk's own
 *  rounding, unless the caller is reconciling (`decimals`). */
export function Figure({ value, asset, decimals }: { value: number; asset: string; decimals?: number }) {
  return (
    <span className={`amt ${value < 0 ? 'text-neg' : ''}`}>
      {fmtAmount(value, asset, decimals)}
    </span>
  );
}

/** A signed movement: the sign law's own "+" and "−", the negative one in
 *  the negative colour; a dash when nothing moved. `code` prints the
 *  currency muted after it, where the block's header does not name it. */
export function Delta({ value, asset, code = false }: { value: number; asset: string; code?: boolean }) {
  if (value === 0) return <span className="text-tg-hint">—</span>;
  return (
    <span className="whitespace-nowrap">
      <span className={`amt ${value < 0 ? 'text-neg' : ''}`}>
        {value > 0 ? '+' : '−'}{fmtAmount(Math.abs(value), asset)}
      </span>
      {code && <> <span className="text-tg-hint">{displayAsset(asset)}</span></>}
    </span>
  );
}

/* ══════════════════ one deal, one row ══════════════════ */

/** What a deal row needs — DealRow and TradeRow both carry it. */
export interface DealLike {
  /** Customer pays. */
  amount_take: string | number;
  asset_take: string;
  /** Customer receives. */
  amount_give: string | number;
  asset_give: string;
  rate: string | number | null;
  booked_at: string;
  /** customer_buy / customer_sell — the verb of the legs. */
  direction?: string | null;
  party_name?: string | null;
  party_slug?: string | null;
  /** The three marks of a manager cheque (isChequeDeal). */
  deal_id?: string | null;
  delivery_method?: string | null;
  payment_method?: string | null;
  lifecycle?: string | null;
}

/** THE CURRENCY A DEAL'S RATE IS QUOTED IN — what the customer pays on a
 *  buy, gets on a sell ("AED" of "buys 10,000 USDT · pays 36,710 AED @
 *  3.671"); the take leg when the row carries no direction. fmtRate reads
 *  the rate at that currency's places. */
export function rateQuote(d: Pick<DealLike, 'direction' | 'asset_take' | 'asset_give'>): string {
  return d.direction === 'customer_sell' ? d.asset_give : d.asset_take;
}

/** The seats at lg+: stamp · party · legs · rate and state (· the action).
 *  Without the party (its own page names it once, above): stamp · legs ·
 *  rate and state · the action, which takes the slack. */
const DEAL_GRID_WITH_PARTY = 'lg:grid-cols-[6.5rem_minmax(0,1fr)_auto_auto]';
const DEAL_GRID_WITH_PARTY_ACT = 'lg:grid-cols-[6.5rem_minmax(0,1fr)_auto_auto_auto]';
const DEAL_GRID_NO_PARTY   = 'lg:grid-cols-[6.5rem_auto_auto_minmax(0,1fr)]';

/** ONE aligned row for a deal wherever deals are listed outside the Trades
 *  log: the stamp · the party · the customer's legs in words ("buys 10,000
 *  USDT · pays 36,710 AED" / "sells 10,000 USDT · gets 36,710 AED"; "pays X
 *  · gets Y" when the row carries no direction) · "@ rate" · a state pill
 *  only when the deal is not settled (settled is the norm and says nothing)
 *  · the screen's own `action` in a trailing seat. A button when it opens
 *  something, else a div. `detail`, when given, is a line of the screen's
 *  own beneath the row (a settle-later row's legs, a refusal panel).
 *  `stacked` keeps the two-line form at every width — a card too narrow
 *  for the grid (Home's Recent trades) would squeeze the party to nothing.
 *
 *  THE TWO-LINE FORM (below lg, and when stacked): line 1 is the stamp, the
 *  party, and the rate pushed to the right end ("17 Sep, 19:33  Hannan ……
 *  @ 3.68"); line 2 is the legs alone, allowed one wrap at the " · " between
 *  them, with the state pill after them when there is one, and the action at
 *  the line's end. The rate and the pill are drawn twice — once for each
 *  form, the other hidden by the breakpoint — because the grid form's seats
 *  are `lg:contents` and cannot move a cell between lines; the grid at lg+
 *  is unchanged. */
export function DealRowLine({ deal, onOpen, hideParty = false, hideState = false, stacked = false, action, detail, className = '' }: {
  deal: DealLike;
  onOpen?: () => void;
  /** The party's own page: its name is the title, not a column. */
  hideParty?: boolean;
  /** A list where every row is in one state (Settle later: awaiting) —
   *  the pill would say the same word on every row. */
  hideState?: boolean;
  /** Two lines at every width. */
  stacked?: boolean;
  action?: ReactNode;
  detail?: ReactNode;
  className?: string;
}) {
  const pays = <Amount value={deal.amount_take} asset={deal.asset_take} />;
  const gets = <Amount value={deal.amount_give} asset={deal.asset_give} />;
  // Each leg is one unbreakable piece, so the two-line form's line 2 can
  // wrap only at the " · " between them — never between a verb and its amount.
  const leg = (verb: string, amt: ReactNode) => <span className="whitespace-nowrap">{verb} {amt}</span>;
  // A CHEQUE IS NOT A TRADE AT RATE 1: the give leg is the cheque's face,
  // the take leg the deposit the customer paid, and the rate says nothing.
  const cheque = isChequeDeal(deal);
  const legs = cheque
    ? <>{leg('cheque', gets)} · {leg('deposit', pays)}</>
    : deal.direction === 'customer_buy'
      ? <>{leg('buys', gets)} · {leg('pays', pays)}</>
      : deal.direction === 'customer_sell'
        ? <>{leg('sells', pays)} · {leg('gets', gets)}</>
        : <>{leg('pays', pays)} · {leg('gets', gets)}</>;
  const state = !hideState && deal.lifecycle && deal.lifecycle !== 'settled' ? deal.lifecycle : null;
  // THE ACTION ON LINE 1 in the two-line form, where there is no party
  // name to make room for: the stamp and the rate leave half the line, and
  // at the end of line 2 the button wrapped under the legs onto a line of
  // its own (Settle, 20 Sept 2026). The line may wrap only for the
  // button's refusal panel, which takes its place and needs the width.
  const actionLead = !!action && hideParty;
  const cols = hideParty ? DEAL_GRID_NO_PARTY : action ? DEAL_GRID_WITH_PARTY_ACT : DEAL_GRID_WITH_PARTY;
  const grid = `${LEDGER.line} ${stacked ? '' : `${LEDGER.lineGrid} ${cols}`} w-full text-left`;
  const seat = stacked ? '' : LEDGER.lineSeat;
  // The two-line form's own pieces: below lg, or at every width when stacked.
  const phoneOnly = stacked ? '' : 'lg:hidden';
  const body = (
    <>
      <div className={`${LEDGER.lineLead} ${actionLead ? 'flex-wrap' : ''} ${seat}`}>
        <span className={`${LEDGER.whenText} ${LEDGER.whenStampW} shrink-0`}>{fmtStamp(deal.booked_at)}</span>
        {!hideParty && (
          <span className="who min-w-0 truncate">{partyName({ display_name: deal.party_name, slug: deal.party_slug })}</span>
        )}
        <span className={`amt text-tg-hint ml-auto shrink-0 whitespace-nowrap ${phoneOnly}`}>{cheque ? '' : `@ ${fmtRate(deal.rate, rateQuote(deal))}`}</span>
        {actionLead && <span className={`shrink-0 max-w-full min-w-0 ${phoneOnly}`}>{action}</span>}
      </div>
      <div className={`${LEDGER.lineTail} ${seat}`}>
        <span className={stacked ? '' : 'lg:whitespace-nowrap'}>
          {legs}
          {state && <Pill tone={lifecycleTone(state)} className={`ml-1.5 ${phoneOnly}`}>{lifecycleLabel(state)}</Pill>}
        </span>
        {!stacked && (
          <span className="hidden lg:flex items-baseline gap-2 whitespace-nowrap">
            <span className="amt text-tg-hint">{cheque ? '' : `@ ${fmtRate(deal.rate, rateQuote(deal))}`}</span>
            {state && <Pill tone={lifecycleTone(state)}>{lifecycleLabel(state)}</Pill>}
          </span>
        )}
        {action && (
          <div className={actionLead ? (stacked ? 'hidden' : 'hidden lg:block lg:justify-self-end') : 'lg:justify-self-end'}>{action}</div>
        )}
      </div>
    </>
  );
  if (onOpen) {
    return (
      <button type="button" onClick={onOpen} className={`${grid} hover:bg-tg-secondary transition-colors ${className}`}>
        {body}
      </button>
    );
  }
  if (detail) {
    return (
      <div className={className}>
        <div className={grid}>{body}</div>
        <div className="px-2 pb-2">{detail}</div>
      </div>
    );
  }
  return <div className={`${grid} ${className}`}>{body}</div>;
}
